import { Box, styled, Tooltip } from '@mui/material';
import React from 'react';
import TableTitle, { TableTitleType } from './TableTitle';
import { isArray } from 'lodash';

const TableContainer = styled(Box)({
  overflowX: 'auto',
});

const Table = styled('table', {
  shouldForwardProp: (prop) =>
    prop !== 'scrollable' &&
    prop !== 'preventHeadingWrap' &&
    prop !== 'isLoading',
})<{ scrollable?: boolean; preventHeadingWrap?: boolean; isLoading?: boolean }>(
  ({ theme, scrollable, preventHeadingWrap, isLoading }) => ({
    display: 'table',
    tableLayout: scrollable ? 'auto' : 'fixed',
    width: '100%',
    fontSize: theme.fontSizes?.sm,
    color: isLoading
      ? theme.palette.text.txtLight
      : theme.palette.text.darkBodyText,
    textAlign: 'left',
    borderCollapse: 'collapse',
    border: `1px solid ${theme.palette.divider}`,

    '& th': {
      whiteSpace: preventHeadingWrap ? 'nowrap' : 'normal',
    },
  }),
);

const TableCell = styled('td', {
  shouldForwardProp: (prop) =>
    prop !== 'background' && prop !== 'showDivider' && prop !== 'isLoading',
})<{
  background?: 'default' | 'white';
  showDivider?: boolean;
  isLoading?: boolean;
}>(({ theme, background, showDivider, isLoading }) => ({
  padding: theme.spacing(4, 5),
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderLeft: showDivider ? `1px solid ${theme.palette.divider}` : 'none',
  backgroundColor:
    background === 'white'
      ? theme.palette.common.white
      : theme.palette.background.header,
  color: isLoading ? theme.palette.grey[500] : undefined,
}));

const TableHeading = styled('th', {
  shouldForwardProp: (prop) =>
    prop !== 'background' && prop !== 'showDivider' && prop !== 'isLoading',
})<{
  background?: 'default' | 'white';
  showDivider?: boolean;
  isLoading?: boolean;
}>(({ theme, background, showDivider, isLoading }) => ({
  padding: theme.spacing(4, 5),
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderLeft: showDivider ? `1px solid ${theme.palette.divider}` : 'none',
  verticalAlign: 'top',
  backgroundColor:
    background === 'white'
      ? theme.palette.common.white
      : theme.palette.background.header,
  color: isLoading ? theme.palette.grey[500] : undefined,
}));

const FooterCell = styled('td')<{ align?: 'left' | 'right' | 'center' }>(
  ({ theme, align = 'left' }) => ({
    padding: theme.spacing(5),
    fontWeight: 'bold',
    textAlign: align,
    backgroundColor: theme.palette.background.header,
  }),
);

type Props<T extends Record<string, React.ReactNode>> = {
  title?: TableTitleType | Array<TableTitleType>;
  columns: Array<{
    key: string;
    label: string;
    render?: (row: T, rowIndex: number) => React.ReactNode;
    showDivider?: boolean;
    tooltip?: string;
  }>;
  rows: Array<T>;
  footer?: Array<{
    key: string;
    value: string;
    align?: 'left' | 'right' | 'center';
    span?: number;
  }>;
  className?: string;
  background?: 'default' | 'white';
  expandedRowContent?: React.ReactNode;
  expandedRowIndex?: number;
  scrollable?: boolean;
  preventHeadingWrap?: boolean;
  isLoading?: boolean;
};

const PreviewTable = <T extends Record<string, React.ReactNode>>({
  title,
  columns,
  rows,
  footer,
  className,
  expandedRowContent,
  expandedRowIndex,
  background = 'default',
  scrollable = false,
  preventHeadingWrap = false,
  isLoading = false,
}: Props<T>): JSX.Element => {
  return (
    <TableContainer className={className}>
      <Table scrollable={scrollable} preventHeadingWrap={preventHeadingWrap}>
        <thead>
          {!!title && (
            <tr>
              <TableTitle
                titles={
                  isArray(title)
                    ? title
                    : [{ ...title, colspan: columns.length }]
                }
                fallbackBackground={background}
              />
            </tr>
          )}
          <tr>
            {columns.map((column) => (
              <TableHeading
                background={background}
                key={column.key}
                showDivider={column.showDivider}
                isLoading={isLoading}
              >
                {column.tooltip ? (
                  <Tooltip title={column.tooltip}>
                    <span>{column.label}</span>
                  </Tooltip>
                ) : (
                  column.label
                )}
              </TableHeading>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <React.Fragment key={`row-${index}`}>
              <tr>
                {columns.map((column) => (
                  <TableCell
                    background={background}
                    showDivider={column.showDivider}
                    key={`cell-${index}-${column.key}`}
                    isLoading={isLoading}
                  >
                    {column.render?.(row, index) ?? row[column.key]}
                  </TableCell>
                ))}
              </tr>
              {expandedRowContent && expandedRowIndex === index && (
                <tr>
                  <TableCell background={background} colSpan={columns.length}>
                    {expandedRowContent}
                  </TableCell>
                </tr>
              )}
            </React.Fragment>
          ))}
          {footer && (
            <tr>
              {footer.map((footerCell) => (
                <FooterCell
                  key={`footer-${footerCell.key}`}
                  colSpan={footerCell.span}
                  align={footerCell.align}
                >
                  {footerCell.value}
                </FooterCell>
              ))}
            </tr>
          )}
        </tbody>
      </Table>
    </TableContainer>
  );
};

export default PreviewTable;
